import React, { useState, useRef, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, DatePicker, message, Space, Tag, Row, Col, Divider, InputNumber, Descriptions, Checkbox, AutoComplete } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, DollarOutlined, PrinterOutlined, MinusCircleOutlined, SettingOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import { useAuthStore } from '../store/authStore';
import api from '../utils/api';
import { printInvoice } from '../utils/printInvoice';
import { getLastPaymentDate } from '../utils/enrollment';
import dayjs from 'dayjs';

const { Option } = Select;

// Suggested emergency-phone owners; users can also type any other value
const PHONE_OWNER_KEYS = ['father', 'mother', 'brother', 'sister', 'relative', 'friend', 'other'];

const normalizeGroup = (group) => {
    if (Array.isArray(group)) return group[0] || undefined;
    return group || undefined;
};

const computeEndDate = (course) => {
    let endDate = new Date();
    if (course) {
        const { value, unit } = course.duration || {};
        if (value && unit) {
            const startDate = new Date();
            if (unit === 'days') endDate = new Date(startDate.getTime() + value * 24 * 60 * 60 * 1000);
            if (unit === 'weeks') endDate = new Date(startDate.getTime() + value * 7 * 24 * 60 * 60 * 1000);
            if (unit === 'months') {
                const d = new Date(startDate);
                d.setMonth(d.getMonth() + value);
                endDate = d;
            }
        }
    }
    return endDate;
};

const buildEnrollment = (course, customFee, group) => {
    if (!course?._id) return null;
    const courseFee = course?.fee?.amount || 0;
    const courseDiscount = course?.discount || 0;
    const defaultFee = courseFee - courseDiscount;
    let actualFee = defaultFee;
    if (customFee !== undefined && customFee !== null && customFee !== '') {
        actualFee = Number(customFee);
    }
    return {
        course: course._id,
        startDate: new Date(),
        endDate: computeEndDate(course),
        status: 'active',
        group: normalizeGroup(group),
        payment: {
            totalFee: actualFee,
            paidAmount: actualFee === 0 ? actualFee : 0,
            remainingAmount: actualFee,
            discount: 0,
            currency: course?.fee?.currency || 'USD',
            paymentStatus: actualFee === 0 ? 'completed' : 'pending',
        },
    };
};

const STUDENT_PROFILE_FIELDS = [
    'firstName', 'lastName', 'email', 'phone', 'dateOfBirth', 'gender',
    'address', 'emergencyContact', 'photo', 'degree', 'school', 'studyLanguage', 'studyingType',
    'classStartHour', 'classEndHour', 'howYouHeardUs', 'howYouHeardUsOther',
    'status', 'notes',
];

const pickProfileFields = (values) => {
    const picked = {};
    STUDENT_PROFILE_FIELDS.forEach((key) => {
        if (key in values) picked[key] = values[key];
    });
    // Strip empty optional fields so mongoose enum validation doesn't fail
    ['email', 'gender', 'studyingType', 'howYouHeardUs', 'howYouHeardUsOther', 'notes', 'degree', 'school', 'studyLanguage'].forEach((key) => {
        if (picked[key] === '' || picked[key] === null || picked[key] === undefined) {
            delete picked[key];
        }
    });
    if (picked.howYouHeardUs !== 'other') {
        delete picked.howYouHeardUsOther;
    }
    if (picked.emergencyContact && !Object.values(picked.emergencyContact).some((v) => v !== '' && v !== null && v !== undefined)) {
        delete picked.emergencyContact;
    }
    return picked;
};

const Students = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { user, updateUser } = useAuthStore();

    // Fetch and update academy data on component mount
    useEffect(() => {
        const fetchAcademyData = async () => {
            if (user) {
                try {
                    const response = await api.get('/academy');
                    if (response.data.data) {
                        // Update user with fresh academy data including logo
                        updateUser({ ...user, academy: response.data.data });
                    }
                } catch (error) {
                    console.error('Failed to fetch academy data:', error);
                }
            }
        };
        fetchAcademyData();
    }, []); // Run only once on mount

    // Helper function to format currency
    const formatCurrency = (amount, currency = 'USD') => {
        const symbol = currency === 'IQD' ? 'IQD' : '$';
        return `${symbol}${amount?.toLocaleString() || '0'}`;
    };

    // Filter states
    const [filterCourse, setFilterCourse] = useState(null);
    const [filterGroup, setFilterGroup] = useState(null);
    const [filterStatus, setFilterStatus] = useState(null);
    const [searchText, setSearchText] = useState('');

    // Certificate printing
    const certificatesPrintRef = useRef();
    const [certificatesToPrint, setCertificatesToPrint] = useState([]);
    // Fetch courses for enrollment
    const { data: coursesData } = useQuery('courses-for-students', async () => {
        const response = await api.get('/courses');
        return response.data.data;
    }, {
        refetchOnMount: 'always',
        refetchOnWindowFocus: false
    });
    // Optional student-form fields the admin chose to hide (shared by the whole academy)
    const { data: hiddenFieldsData } = useQuery('student-hidden-fields', async () => {
        const response = await api.get('/academy');
        return response.data.data?.hiddenStudentFields || [];
    }, { refetchOnWindowFocus: false });
    const hiddenFields = hiddenFieldsData || [];
    const fieldCol = (key) => (hiddenFields.includes(key) ? { style: { display: 'none' } } : {});
    const [isFieldsModalVisible, setIsFieldsModalVisible] = useState(false);
    const [pendingHiddenFields, setPendingHiddenFields] = useState([]);
    const saveHiddenFieldsMutation = useMutation(
        (fields) => api.put('/academy', { hiddenStudentFields: fields }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('student-hidden-fields');
                message.success(t('updatedSuccessfully'));
                setIsFieldsModalVisible(false);
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToUpdate'));
            },
        }
    );
    const toggleableFields = [
        { key: 'email', label: t('email') },
        { key: 'emergencyPhone', label: t('emergencyPhone') },
        { key: 'emergencyPhoneOwner', label: t('emergencyPhoneOwner') },
        { key: 'school', label: t('school') },
        { key: 'studyLanguage', label: t('studyLanguage') },
        { key: 'dateOfBirth', label: t('dateOfBirth') },
        { key: 'gender', label: t('gender') },
        { key: 'degree', label: t('degree') },
        { key: 'studyingType', label: t('studyingType') },
        { key: 'classStartHour', label: t('classStartHour') },
        { key: 'classEndHour', label: t('classEndHour') },
        { key: 'howYouHeardUs', label: t('howYouHeardUs') },
        { key: 'notes', label: t('notes') },
    ];
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingStudent, setEditingStudent] = useState(null);
    // Enrollments the user removed in the edit dialog, applied when the form is saved
    const [removedEnrollmentIds, setRemovedEnrollmentIds] = useState([]);
    const [form] = Form.useForm();
    // Watching the list re-renders the form whenever a course/group changes,
    // so a course's groups are offered as soon as the course is picked.
    const watchedAssignments = Form.useWatch('courseAssignments', form);
    const getGroupOptions = (assignment) => {
        const course = coursesData?.find((c) => c._id === assignment?.course);
        const groups = new Set(course?.groups || []);
        const current = Array.isArray(assignment?.group) ? assignment.group[0] : assignment?.group;
        if (current) groups.add(current);
        return Array.from(groups).map((g) => ({ value: g, label: g }));
    };
    const queryClient = useQueryClient();

    // Payment modal state
    const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
    const [selectedEnrollment, setSelectedEnrollment] = useState(null);
    const [paymentForm] = Form.useForm();
    const [paymentCalculation, setPaymentCalculation] = useState({ amount: 0, discount: 0, actualPayment: 0, newRemaining: 0 });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { data: studentsData, isLoading } = useQuery('students', async () => {
        const response = await api.get('/students', {
            params: { limit: 10000 }
        });
        return response.data.data;
    }, {
        refetchOnMount: 'always',
        refetchOnWindowFocus: false
    });

    // Fetch certificates for printing
    const { data: certificatesData } = useQuery('certificates-for-students', async () => {
        const response = await api.get('/certificates');
        return response.data.data;
    });

    // Fetch default certificate template
    const { data: templateData } = useQuery('certificate-template-for-students', async () => {
        try {
            const response = await api.get('/certificates/template/default');
            return response.data.data;
        } catch (error) {
            return null;
        }
    });

    const handlePrintCertificates = useReactToPrint({
        contentRef: certificatesPrintRef,
        documentTitle: `Certificates-${filterCourse ? coursesData?.find(c => c._id === filterCourse)?.name : 'All'}`,
    });

    const createMutation = useMutation(
        (data) => api.post('/students', data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('students');
                queryClient.refetchQueries('students');
                queryClient.refetchQueries('courses-for-students');
                message.success(t('createdSuccessfully'));
                setIsModalVisible(false);
                form.resetFields();
            },
            onError: (error) => {
                const data = error.response?.data;
                if (data?.errorKey === 'duplicateStudentExists') {
                    message.error(t('duplicateStudentExists', { name: data.studentName, id: data.existingStudentId }));
                } else {
                    message.error(data?.message || t('failedToCreateStudent'));
                }
            },
        }
    );

    const deleteMutation = useMutation(
        (id) => api.delete(`/students/${id}`),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('students');
                message.success(t('deletedSuccessfully'));
            },
            onError: (error) => {
                message.error(error.response?.data?.message || 'Failed to delete student');
            },
        }
    );

    const paymentMutation = useMutation(
        ({ studentId, enrollmentId, data }) =>
            api.post(`/students/${studentId}/payment/${enrollmentId}`, data),
        {
            onSuccess: (response) => {
                queryClient.invalidateQueries('students');
                message.success(t('paymentRecordedSuccessfully'));
                setIsPaymentModalVisible(false);
                setSelectedEnrollment(null);
                paymentForm.resetFields();

                // Auto-print receipt
                printReceipt(response.data.data);
            },
            onError: (error) => {
                console.error('Payment error:', error);
                console.error('Error response:', error.response);
                message.error(error.response?.data?.message || 'Failed to record payment');
            },
        }
    );

    const printReceipt = (paymentData) => {
        const { student, income } = paymentData;
        printInvoice({
            invoice: income,
            student,
            user,
            t,
            language: i18n.language
        });
    };

    const handleSubmit = async (values) => {
        const formattedValues = pickProfileFields({
            ...values,
            dateOfBirth: values.dateOfBirth ? values.dateOfBirth.toISOString() : null,
        });

        const validAssignments = (values.courseAssignments || []).filter((a) => a?.course);
        if (editingStudent) {
            const enrollmentMeta = (values.courseAssignments || [])
                .filter((a) => a.enrollmentId)
                .map((a) => ({ enrollmentId: a.enrollmentId, group: normalizeGroup(a.group) }));

            // Rows without an enrollmentId are new enrollments (same course may repeat)
            const newAssignments = validAssignments.filter((a) => !a.enrollmentId);

            formattedValues.enrollmentMeta = enrollmentMeta;

            try {
                setIsSubmitting(true);
                await api.put(`/students/${editingStudent._id}`, formattedValues);

                // Remove courses the user dropped. Income/invoice records are kept by the API.
                for (const enrollmentId of removedEnrollmentIds) {
                    await api.delete(`/students/${editingStudent._id}/enrollments/${enrollmentId}`);
                }

                for (const assignment of newAssignments) {
                    const selectedCourse = coursesData?.find((c) => c._id === assignment.course);
                    const courseFee = selectedCourse?.fee?.amount || 0;
                    const courseDiscount = selectedCourse?.discount || 0;
                    const defaultFee = courseFee - courseDiscount;
                    let actualFee = defaultFee;
                    if (assignment.customFee !== undefined && assignment.customFee !== null && assignment.customFee !== '') {
                        actualFee = Number(assignment.customFee);
                    }

                    await api.post(`/students/${editingStudent._id}/enroll`, {
                        courseId: assignment.course,
                        startDate: new Date(),
                        endDate: computeEndDate(selectedCourse),
                        totalFee: actualFee,
                        group: normalizeGroup(assignment.group),
                    });
                }

                queryClient.invalidateQueries('students');
                queryClient.refetchQueries('students');
                queryClient.refetchQueries('courses-for-students');
                message.success(t('updatedSuccessfully'));
                setIsModalVisible(false);
                setEditingStudent(null);
                setRemovedEnrollmentIds([]);
                form.resetFields();
            } catch (error) {
                message.error(error.response?.data?.message || 'Failed to update student');
            } finally {
                setIsSubmitting(false);
            }
        } else {
            if (validAssignments.length) {
                const builtEnrollments = validAssignments
                    .map((assignment) => {
                        const selectedCourse = coursesData?.find((c) => c._id === assignment.course);
                        return buildEnrollment(selectedCourse, assignment.customFee, assignment.group);
                    })
                    .filter(Boolean);

                if (builtEnrollments.length !== validAssignments.length) {
                    message.error(t('courseNotFound') || 'One or more selected courses could not be found');
                    return;
                }

                formattedValues.enrollments = builtEnrollments;
            }
            createMutation.mutate(formattedValues);
        }
    };

    const handleEdit = (record) => {
        setEditingStudent(record);
        setRemovedEnrollmentIds([]);
        const activeEnrollments = record.enrollments?.filter((en) => en.status === 'active') || [];
        form.setFieldsValue({
            ...record,
            dateOfBirth: record.dateOfBirth ? dayjs(record.dateOfBirth) : null,
            degree: record.degree,
            studyingType: record.studyingType,
            classStartHour: record.classStartHour,
            classEndHour: record.classEndHour,
            howYouHeardUs: record.howYouHeardUs,
            howYouHeardUsOther: record.howYouHeardUsOther,
            notes: record.notes,
            courseAssignments: activeEnrollments.length > 0
                ? activeEnrollments.map((en) => ({
                    enrollmentId: en._id,
                    course: en.course?._id || en.course,
                    customFee: en.payment?.totalFee,
                    group: en.group ? [en.group] : undefined,
                }))
                : [],
        });
        setIsModalVisible(true);
    };

    const handleRemoveEnrollment = (enrollmentId, fieldName, remove) => {
        Modal.confirm({
            title: t('confirmDelete'),
            content: `${t('removeCourseConfirm')} ${t('removeCourseOnSave')}`,
            okText: t('yes'),
            okType: 'danger',
            cancelText: t('no'),
            onOk: () => {
                setRemovedEnrollmentIds((prev) =>
                    prev.includes(enrollmentId) ? prev : [...prev, enrollmentId]
                );
                remove(fieldName);
            },
        });
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: t('confirmDelete'),
            content: t('actionCannotBeUndone'),
            onOk: () => deleteMutation.mutate(id),
        });
    };

    const handlePayment = (student, enrollment) => {
        setSelectedEnrollment({ student, enrollment });

        // Get course to calculate correct fee (with course-level discount applied)
        const courseObj = coursesData?.find(c => c._id === (enrollment.course?._id || enrollment.course));
        const originalCourseFee = courseObj?.fee?.amount || 0;
        const courseDiscount = courseObj?.discount || 0;

        // Correct total fee = original course fee - course-level discount
        const correctTotalFee = originalCourseFee - courseDiscount;

        // Get payment status from enrollment
        const paidAmount = enrollment.payment?.paidAmount || 0;
        const discountGiven = enrollment.payment?.discount || 0;

        // Remaining = Correct Total Fee - (Paid + Discounts Given)
        const currentRemaining = correctTotalFee - paidAmount - discountGiven;
        const enrollmentCurrency = enrollment.payment?.currency || 'IQD';

        paymentForm.setFieldsValue({
            currency: enrollmentCurrency,
            paymentMethod: 'cash',
            paymentDate: dayjs(),
            amount: currentRemaining,
            discount: 0
        });

        setPaymentCalculation({
            courseFee: correctTotalFee,
            paidAmount: paidAmount,
            discountGiven: discountGiven,
            remainingAmount: currentRemaining,
            discount: 0,
            actualPayment: currentRemaining,
            newRemaining: 0
        });

        setIsPaymentModalVisible(true);
    };

    const handlePaymentSubmit = (values) => {
        const paymentAmount = values.amount || 0;
        const discountAmount = values.discount || 0;

        console.log('Payment form values:', values);
        console.log('Payment amount:', paymentAmount, 'Discount:', discountAmount);

        const paymentData = {
            amount: paymentAmount, // Actual cash payment
            discount: discountAmount, // Additional discount on this payment
            currency: selectedEnrollment.enrollment.payment?.currency || 'USD',
            paymentDate: values.paymentDate ? values.paymentDate.toISOString() : new Date().toISOString(),
            paymentMethod: values.paymentMethod || 'cash',
            description: values.description || '',
            paymentDeadline: values.paymentDeadline ? values.paymentDeadline.toISOString() : null
        };

        console.log('Sending payment data:', paymentData);

        paymentMutation.mutate({
            studentId: selectedEnrollment.student._id,
            enrollmentId: selectedEnrollment.enrollment._id,
            data: paymentData
        });
    };

    // Calculate total after discount
    const calculateTotalAfterDiscount = () => {
        const amount = paymentForm.getFieldValue('amount') || 0;
        return amount; // The amount already has discount applied
    };

    const columns = [
        {
            title: t('studentId'),
            dataIndex: 'studentId',
            key: 'studentId',
        },
        {
            title: t('name'),
            key: 'name',
            render: (_, record) => (
                <a
                    onClick={() => navigate(`/students/${record._id}`)}
                    style={{ cursor: 'pointer', color: '#1890ff' }}
                >
                    {record.firstName} {record.lastName}
                </a>
            ),
        },
        {
            title: t('phone'),
            dataIndex: 'phone',
            key: 'phone',
        },
        {
            title: t('enrolledCourses'),
            key: 'courses',
            width: 340,
            render: (_, record) => (
                <div>
                    {record.enrollments && record.enrollments.filter(en => en.status !== 'cancelled').length > 0
                        ? record.enrollments.filter(en => en.status !== 'cancelled').map((en, idx) => {
                            const courseObj = coursesData?.find(c => c._id === (en.course?._id || en.course));

                            // Calculate correct remaining amount considering course discount and payment discounts
                            const originalCourseFee = courseObj?.fee?.amount || 0;
                            const courseDiscount = courseObj?.discount || 0;
                            const discountedCourseFee = originalCourseFee - courseDiscount;
                            const paidAmount = en.payment?.paidAmount || 0;
                            const discountGiven = en.payment?.discount || 0;
                            const correctRemainingAmount = Math.max(0, discountedCourseFee - paidAmount - discountGiven);

                            const isPending = correctRemainingAmount > 0;
                            const lastPaymentDate = getLastPaymentDate(en.payment);
                            return (
                                <div key={idx} style={{ marginBottom: 8 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                        <Tag color="blue" style={{ margin: 0 }}>
                                            {courseObj ? `${courseObj.name} (${courseObj.code})` : 'Course'}
                                        </Tag>
                                        {en.group && (
                                            <Tag color="purple" style={{ margin: 0 }}>{en.group}</Tag>
                                        )}
                                        {isPending && (
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<DollarOutlined />}
                                                onClick={() => handlePayment(record, en)}
                                                style={{ padding: '0 4px', height: 'auto' }}
                                            >
                                                Pay {formatCurrency(correctRemainingAmount, en.payment.currency)}
                                            </Button>
                                        )}
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: 2 }}>
                                        {t('registrationDate')}: {en.startDate ? dayjs(en.startDate).format('YYYY-MM-DD') : '-'}
                                        {' · '}
                                        {t('lastPaymentDate')}: {lastPaymentDate ? dayjs(lastPaymentDate).format('YYYY-MM-DD') : '-'}
                                    </div>
                                </div>
                            );
                        })
                        : <Tag color="default">None</Tag>
                    }
                </div>
            ),
        },
        {
            title: t('paidAmount') || 'Paid Amount',
            key: 'totalPaid',
            width: 120,
            align: 'right',
            render: (_, record) => {
                const activeEnrollments = record.enrollments?.filter(en => en.status !== 'cancelled') || [];
                const totalPaid = activeEnrollments.reduce((sum, en) =>
                    sum + (en.payment?.paidAmount || 0), 0) || 0;
                const currency = activeEnrollments[0]?.payment?.currency || record.enrollments?.[0]?.payment?.currency || 'USD';
                return <span style={{ fontWeight: 600, color: '#15803d' }}>{formatCurrency(totalPaid, currency)}</span>;
            },
        },
        // {
        //     title: t('courseFee') || 'Course Fee',
        //     key: 'totalFee',
        //     width: 150,
        //     align: 'right',
        //     render: (_, record) => {
        //         let totalOriginalFee = 0;
        //         let totalDiscountedFee = 0;
        //         let totalDiscount = 0;
        //         const currency = record.enrollments?.[0]?.payment?.currency || 'USD';

        //         record.enrollments?.forEach(en => {
        //             const courseObj = coursesData?.find(c => c._id === (en.course?._id || en.course));
        //             const originalFee = courseObj?.fee?.amount || 0;
        //             const courseDiscount = courseObj?.discount || 0;
        //             const finalFee = en.payment?.totalFee || (originalFee - courseDiscount);

        //             totalOriginalFee += originalFee;
        //             totalDiscountedFee += finalFee;
        //             totalDiscount += (originalFee - finalFee);
        //         });

        //         if (totalDiscount > 0) {
        //             return (
        //                 <div style={{ textAlign: 'right' }}>
        //                     <div style={{ textDecoration: 'line-through', color: '#999', fontSize: '12px' }}>
        //                         {formatCurrency(totalOriginalFee, currency)}
        //                     </div>
        //                     <div style={{ color: '#15803d', fontWeight: 'bold' }}>
        //                         {formatCurrency(totalDiscountedFee, currency)}
        //                     </div>
        //                 </div>
        //             );
        //         }
        //         return <span style={{ fontWeight: 600 }}>{formatCurrency(totalDiscountedFee, currency)}</span>;
        //     },
        // },

        {
            title: t('discount') || 'Discount',
            key: 'totalDiscount',
            width: 120,
            align: 'right',
            render: (_, record) => {
                const activeEnrollments = record.enrollments?.filter(en => en.status !== 'cancelled') || [];
                const totalDiscount = activeEnrollments.reduce((sum, en) =>
                    sum + (en.payment?.discount || 0), 0) || 0;
                const currency = activeEnrollments[0]?.payment?.currency || record.enrollments?.[0]?.payment?.currency || 'USD';
                return <span style={{ fontWeight: 600, color: '#2563eb' }}>{formatCurrency(totalDiscount, currency)}</span>;
            },
        },
        {
            title: t('remaining') || 'Remaining',
            key: 'totalRemaining',
            width: 120,
            align: 'right',
            render: (_, record) => {
                const activeEnrollments = record.enrollments?.filter(en => en.status !== 'cancelled') || [];
                const totalRemaining = activeEnrollments.reduce((sum, en) => {
                    const courseObj = coursesData?.find(c => c._id === (en.course?._id || en.course));
                    const originalCourseFee = courseObj?.fee?.amount || 0;
                    const courseDiscount = courseObj?.discount || 0;
                    const discountedCourseFee = originalCourseFee - courseDiscount;
                    const paidAmount = en.payment?.paidAmount || 0;
                    const discountGiven = en.payment?.discount || 0;
                    const correctRemainingAmount = Math.max(0, discountedCourseFee - paidAmount - discountGiven);
                    return sum + correctRemainingAmount;
                }, 0) || 0;
                const currency = activeEnrollments[0]?.payment?.currency || record.enrollments?.[0]?.payment?.currency || 'USD';
                return <span style={{ fontWeight: 600, color: '#dc2626' }}>{formatCurrency(totalRemaining, currency)}</span>;
            },
        },
        {
            title: t('notes'),
            dataIndex: 'notes',
            key: 'notes',
            width: 180,
            ellipsis: true,
            render: (notes) => notes ? (
                <span title={notes} style={{ color: '#64748b', fontSize: '12px' }}>{notes}</span>
            ) : '-',
        },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => {
                const color = {
                    active: 'green',
                    inactive: 'orange',
                    graduated: 'blue',
                    withdrawn: 'red',
                }[status];
                return <Tag color={color}>{t(status) || status}</Tag>;
            },
        },
        {
            title: t('actions'),
            key: 'actions',
            fixed: 'right',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    {(user?.role === 'admin' || user?.role === 'superadmin') && (
                        <Button
                            icon={<DeleteOutlined />}
                            danger
                            onClick={() => handleDelete(record._id)}
                        />
                    )}
                </Space>
            ),
        },
    ];

    // Filtering logic
    let filteredStudents = studentsData || [];
    if (filterCourse) {
        filteredStudents = filteredStudents.filter((student) =>
            student.enrollments?.some(
                (en) => (en.course?._id || en.course) === filterCourse && en.status === 'active'
            )
        );
    }
    if (filterGroup && filterCourse) {
        filteredStudents = filteredStudents.filter((student) =>
            student.enrollments?.some(
                (en) =>
                    (en.course?._id || en.course) === filterCourse &&
                    en.status === 'active' &&
                    en.group === filterGroup
            )
        );
    }
    if (filterStatus) {
        filteredStudents = filteredStudents.filter((student) => student.status === filterStatus);
    }
    if (searchText) {
        const searchLower = searchText.toLowerCase().trim();
        const searchWords = searchLower.split(/\s+/).filter(Boolean);

        // Filter students that match the search
        filteredStudents = filteredStudents.filter(student => {
            const firstName = (student.firstName || '').toLowerCase();
            const lastName = (student.lastName || '').toLowerCase();
            const fullName = (firstName + ' ' + lastName);
            const reversedName = (lastName + ' ' + firstName);
            const email = (student.email || '').toLowerCase();
            const phone = (student.phone || '').toLowerCase();
            const studentId = (student.studentId || '').toLowerCase();

            // Check if all search words appear somewhere in student data
            const allWordsMatch = searchWords.every(word =>
                firstName.includes(word) ||
                lastName.includes(word) ||
                email.includes(word) ||
                phone.includes(word) ||
                studentId.includes(word)
            );

            return allWordsMatch ||
                fullName.includes(searchLower) ||
                reversedName.includes(searchLower) ||
                phone.includes(searchLower) ||
                studentId.includes(searchLower);
        });

        // Sort: names starting with search term first, then others
        filteredStudents = filteredStudents.sort((a, b) => {
            const fullNameA = (a.firstName + ' ' + a.lastName).toLowerCase();
            const fullNameB = (b.firstName + ' ' + b.lastName).toLowerCase();
            const firstNameA = a.firstName.toLowerCase();
            const firstNameB = b.firstName.toLowerCase();
            const lastNameA = a.lastName.toLowerCase();
            const lastNameB = b.lastName.toLowerCase();

            // Check if full name starts with search term
            const aFullNameStarts = fullNameA.startsWith(searchLower);
            const bFullNameStarts = fullNameB.startsWith(searchLower);

            // Check if first name starts with search term
            const aFirstNameStarts = firstNameA.startsWith(searchLower);
            const bFirstNameStarts = firstNameB.startsWith(searchLower);

            // Check if last name starts with search term
            const aLastNameStarts = lastNameA.startsWith(searchLower);
            const bLastNameStarts = lastNameB.startsWith(searchLower);

            // Priority order:
            // 1. Full name starts with search
            // 2. First name starts with search
            // 3. Last name starts with search
            // 4. Contains search term (already filtered)

            if (aFullNameStarts && !bFullNameStarts) return -1;
            if (!aFullNameStarts && bFullNameStarts) return 1;

            if (aFirstNameStarts && !bFirstNameStarts) return -1;
            if (!aFirstNameStarts && bFirstNameStarts) return 1;

            if (aLastNameStarts && !bLastNameStarts) return -1;
            if (!aLastNameStarts && bLastNameStarts) return 1;

            // If both have same priority, sort alphabetically
            return fullNameA.localeCompare(fullNameB);
        });
    }

    const selectedCourseGroups = filterCourse
        ? (coursesData?.find((c) => c._id === filterCourse)?.groups || [])
        : [];
    const studentGroupsForCourse = filterCourse
        ? [...new Set(
            (studentsData || [])
                .flatMap((s) => s.enrollments || [])
                .filter((en) => (en.course?._id || en.course) === filterCourse && en.status === 'active' && en.group)
                .map((en) => en.group)
        )]
        : [];
    const availableGroups = [...new Set([...selectedCourseGroups, ...studentGroupsForCourse])];

    return (
        <div>
            <div className="page-header">
                <h1>{t('students')}</h1>
                <Space className="filter-section" wrap>
                    <Select
                        placeholder={t('filterByCourse')}
                        allowClear
                        style={{ width: 350 }}
                        value={filterCourse}
                        onChange={(value) => {
                            setFilterCourse(value || null);
                            setFilterGroup(null);
                        }}
                    >
                        {coursesData?.map(course => (
                            <Option key={course._id} value={course._id}>
                                {course.name} ({course.code})
                            </Option>
                        ))}
                    </Select>
                    {filterCourse && availableGroups.length > 0 && (
                        <Select
                            placeholder={t('filterByGroup')}
                            allowClear
                            style={{ width: 200 }}
                            value={filterGroup}
                            onChange={(value) => setFilterGroup(value || null)}
                        >
                            {availableGroups.map((group) => (
                                <Option key={group} value={group}>{group}</Option>
                            ))}
                        </Select>
                    )}
                    <Select
                        placeholder={t('filterByStatus')}
                        allowClear
                        style={{ width: 220 }}
                        value={filterStatus}
                        onChange={(value) => setFilterStatus(value || null)}
                    >
                        <Option value="active">{t('active')}</Option>
                        <Option value="inactive">{t('inactive')}</Option>
                        <Option value="graduated">{t('graduated')}</Option>
                        <Option value="withdrawn">{t('withdrawn')}</Option>
                    </Select>
                    <Input.Search
                        placeholder={t('searchByNameOrPhone') || 'Search by name or phone'}
                        allowClear
                        style={{ width: 320 }}
                        onSearch={value => setSearchText(value)}
                        onChange={e => setSearchText(e.target.value)}
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditingStudent(null);
                            setRemovedEnrollmentIds([]);
                            form.resetFields();
                            form.setFieldsValue({ courseAssignments: [], status: 'active' });
                            setIsModalVisible(true);
                        }}
                    >
                        {t('addStudent')}
                    </Button>
                    {filterCourse && (
                        <Button
                            type="default"
                            icon={<PrinterOutlined />}
                            onClick={() => {
                                // Get certificates for filtered students
                                const studentIds = filteredStudents.map(s => s._id);
                                const certs = certificatesData?.filter(cert =>
                                    studentIds.includes(cert.student?._id) &&
                                    cert.course?._id === filterCourse
                                ) || [];

                                if (certs.length === 0) {
                                    message.warning(t('noCertificatesFound'));
                                    return;
                                }

                                setCertificatesToPrint(certs);
                                setTimeout(() => handlePrintCertificates(), 100);
                            }}
                        >
                            {t('printCertificates')}
                        </Button>
                    )}
                </Space>
            </div>

            <Table
                columns={columns}
                dataSource={filteredStudents}
                rowKey="_id"
                loading={isLoading}
                scroll={{ x: 1400 }}
                size="small"
                style={{ fontSize: '12px' }}
            />

            <Modal
                title={t('customizeFormFields')}
                open={isFieldsModalVisible}
                onCancel={() => setIsFieldsModalVisible(false)}
                onOk={() => saveHiddenFieldsMutation.mutate(pendingHiddenFields)}
                confirmLoading={saveHiddenFieldsMutation.isLoading}
                okText={t('save')}
                cancelText={t('cancel')}
                centered
            >
                <p style={{ color: '#64748b' }}>{t('customizeFormFieldsHint')}</p>
                <Space direction="vertical">
                    {toggleableFields.map((f) => (
                        <Checkbox
                            key={f.key}
                            checked={!pendingHiddenFields.includes(f.key)}
                            onChange={(e) => setPendingHiddenFields((prev) =>
                                e.target.checked ? prev.filter((k) => k !== f.key) : [...prev, f.key]
                            )}
                        >
                            {f.label}
                        </Checkbox>
                    ))}
                </Space>
            </Modal>

            <Modal
                title={editingStudent ? t('editStudent') : t('addStudent')}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setEditingStudent(null);
                    setRemovedEnrollmentIds([]);
                    form.resetFields();
                }}
                footer={null}
                width="min(800px, 95vw)"
                centered
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    onFinishFailed={({ errorFields }) => {
                        const firstError = errorFields?.[0]?.errors?.[0];
                        if (firstError) message.error(firstError);
                    }}
                >
                    {user?.role === 'admin' && (
                        <div style={{ textAlign: 'end', marginBottom: 8 }}>
                            <Button
                                size="small"
                                icon={<SettingOutlined />}
                                onClick={() => {
                                    setPendingHiddenFields(hiddenFields);
                                    setIsFieldsModalVisible(true);
                                }}
                            >
                                {t('customizeFormFields')}
                            </Button>
                        </div>
                    )}

                    {/* <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="studentId" label={t('studentId')}>
                                <Input
                                    placeholder={t('autoGeneratedIfLeftBlank')}
                                    size="large"
                                    prefix={<span style={{ color: '#94a3b8' }}>ID:</span>}
                                />
                            </Form.Item>
                        </Col>
                    </Row> */}

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="firstName"
                                label={t('firstName')}
                                rules={[{ required: true, message: t('pleaseEnterFirstName') }]}
                            >
                                <Input size="large" placeholder={t('firstName')} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="lastName"
                                label={t('lastName')}
                                rules={[{ required: true, message: t('pleaseEnterLastName') }]}
                            >
                                <Input size="large" placeholder={t('lastName')} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8} {...fieldCol('email')}>
                            <Form.Item
                                name="email"
                                label={t('email')}
                                rules={[
                                    {
                                        validator: (_, value) => {
                                            if (!value || !String(value).trim()) return Promise.resolve();
                                            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                            if (!emailPattern.test(String(value).trim())) {
                                                return Promise.reject(new Error(t('pleaseEnterValidEmail')));
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Input size="large" placeholder="example@email.com" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="phone"
                                label={t('phone')}
                                rules={[{ required: true, message: t('pleaseEnterPhone') }]}
                            >
                                <Input size="large" placeholder="+964 750 123 4567" />
                            </Form.Item>
                        </Col>
                        <Col span={8} {...fieldCol('emergencyPhone')}>
                            <Form.Item
                                name={['emergencyContact', 'phone']}
                                label={t('emergencyPhone')}
                            >
                                <Input size="large" placeholder="+964 750 123 4567" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8} {...fieldCol('emergencyPhoneOwner')}>
                            <Form.Item
                                name={['emergencyContact', 'relationship']}
                                label={t('emergencyPhoneOwner')}
                                getValueProps={(v) => ({ value: PHONE_OWNER_KEYS.includes(v) ? t(v) : v })}
                                normalize={(v) => PHONE_OWNER_KEYS.find((k) => t(k) === v) || v}
                            >
                                <AutoComplete
                                    size="large"
                                    allowClear
                                    placeholder={t('selectEmergencyPhoneOwner')}
                                    options={PHONE_OWNER_KEYS.map((k) => ({ value: t(k), label: t(k) }))}
                                    filterOption={(input, option) =>
                                        String(option?.value || '').toLowerCase().includes(String(input || '').toLowerCase())
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8} {...fieldCol('school')}>
                            <Form.Item name="school" label={t('school')}>
                                <Input size="large" placeholder={t('enterSchool')} />
                            </Form.Item>
                        </Col>
                        <Col span={8} {...fieldCol('studyLanguage')}>
                            <Form.Item name="studyLanguage" label={t('studyLanguage')}>
                                <Select size="large" placeholder={t('selectStudyLanguage')} allowClear>
                                    <Option value="english">{t('english')}</Option>
                                    <Option value="kurdish">{t('kurdish')}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12} {...fieldCol('dateOfBirth')}>
                            <Form.Item name="dateOfBirth" label={t('dateOfBirth')}>
                                <DatePicker
                                    style={{ width: '100%' }}
                                    size="large"
                                    placeholder={t('selectDate')}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12} {...fieldCol('gender')}>
                            <Form.Item name="gender" label={t('gender')}>
                                <Select size="large" placeholder={t('selectGender')}>
                                    <Option value="male">{t('male')}</Option>
                                    <Option value="female">{t('female')}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12} {...fieldCol('degree')}>
                            <Form.Item name="degree" label={t('degree')}>
                                <Input size="large" placeholder={t('degree')} />
                            </Form.Item>
                        </Col>
                        <Col span={12} {...fieldCol('studyingType')}>
                            <Form.Item name="studyingType" label={t('studyingType')}>
                                <Select size="large" placeholder={t('selectStudyingType')} allowClear>
                                    <Option value="in-class">{t('inClass')}</Option>
                                    <Option value="live-online">{t('liveOnline')}</Option>
                                    <Option value="vip">{t('vip')}</Option>
                                    <Option value="recorded">{t('recorded')}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12} {...fieldCol('classStartHour')}>
                            <Form.Item name="classStartHour" label={t('classStartHour')}>
                                <Input size="large" type="time" placeholder="09:00" />
                            </Form.Item>
                        </Col>
                        <Col span={12} {...fieldCol('classEndHour')}>
                            <Form.Item name="classEndHour" label={t('classEndHour')}>
                                <Input size="large" type="time" placeholder="11:00" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12} {...fieldCol('howYouHeardUs')}>
                            <Form.Item name="howYouHeardUs" label={t('howYouHeardUs')}>
                                <Select size="large" placeholder={t('selectHowYouHeardUs')} allowClear>
                                    <Option value="instagram">{t('instagram')}</Option>
                                    <Option value="tiktok">{t('tiktok')}</Option>
                                    <Option value="facebook">{t('facebook')}</Option>
                                    <Option value="friend">{t('friend')}</Option>
                                    <Option value="other">{t('other')}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12} {...fieldCol('howYouHeardUs')}>
                            <Form.Item
                                name="howYouHeardUsOther"
                                label={t('howYouHeardUsOther')}
                                dependencies={['howYouHeardUs']}
                                rules={[
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (getFieldValue('howYouHeardUs') === 'other' && !value) {
                                                return Promise.reject(new Error(t('pleaseSpecifyOther')));
                                            }
                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                            >
                                <Input size="large" placeholder={t('specifyOther')} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider orientation="left">{t('assignCourses')}</Divider>
                    <Form.List name="courseAssignments">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => {
                                    const enrollmentId = form.getFieldValue(['courseAssignments', name, 'enrollmentId']);
                                    const enrolledFieldCount = fields.filter(
                                        (f) => form.getFieldValue(['courseAssignments', f.name, 'enrollmentId'])
                                    ).length;
                                    return (
                                        <Row gutter={16} key={key} align="middle" style={{ marginBottom: 8 }}>
                                            <Col span={8}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'course']}
                                                    label={t('course')}
                                                >
                                                    <Select
                                                        size="large"
                                                        placeholder={t('selectCourse')}
                                                        allowClear
                                                        disabled={!!enrollmentId}
                                                        onChange={(value) => {
                                                            if (value) {
                                                                const course = coursesData?.find((c) => c._id === value);
                                                                const courseFee = course?.fee?.amount || 0;
                                                                const courseDiscount = course?.discount || 0;
                                                                form.setFieldValue(['courseAssignments', name, 'customFee'], courseFee - courseDiscount);
                                                                form.setFieldValue(['courseAssignments', name, 'group'], undefined);
                                                            }
                                                        }}
                                                    >
                                                        {coursesData?.map((course) => (
                                                            <Option key={course._id} value={course._id}>
                                                                {course.name} ({course.code})
                                                            </Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col span={5}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'group']}
                                                    label={t('group')}
                                                >
                                                    <Select
                                                        size="large"
                                                        placeholder={t('selectGroup')}
                                                        allowClear
                                                        showSearch
                                                        mode="tags"
                                                        maxCount={1}
                                                        options={getGroupOptions(watchedAssignments?.[name])}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={7}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'customFee']}
                                                    label={t('customFee') || 'Custom Fee'}
                                                    tooltip={t('customFeeTooltip') || 'Override the course fee. Set to 0 for free.'}
                                                >
                                                    <InputNumber
                                                        size="large"
                                                        style={{ width: '100%' }}
                                                        min={0}
                                                        disabled={!!enrollmentId}
                                                        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                        parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={2}>
                                                <Form.Item {...restField} name={[name, 'enrollmentId']} hidden>
                                                    <Input />
                                                </Form.Item>
                                                {enrollmentId ? (
                                                    <Button
                                                        type="text"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        disabled={enrolledFieldCount <= 1}
                                                        title={enrolledFieldCount <= 1 ? t('cannotRemoveLastCourse') : t('removeCourse')}
                                                        onClick={() => handleRemoveEnrollment(enrollmentId, name, remove)}
                                                        style={{ marginTop: 30 }}
                                                    />
                                                ) : (
                                                    <Button
                                                        type="text"
                                                        danger
                                                        icon={<MinusCircleOutlined />}
                                                        onClick={() => remove(name)}
                                                        style={{ marginTop: 30 }}
                                                    />
                                                )}
                                            </Col>
                                        </Row>
                                    );
                                })}
                                <Form.Item>
                                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                        {t('addCourse')}
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="status" label={t('status')} initialValue="active">
                                <Select size="large" placeholder={t('selectStatus')}>
                                    <Option value="active">{t('active')}</Option>
                                    <Option value="inactive">{t('inactive')}</Option>
                                    <Option value="graduated">{t('graduated')}</Option>
                                    <Option value="withdrawn">{t('withdrawn')}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="notes" label={t('notes')} hidden={hiddenFields.includes('notes')}>
                        <Input.TextArea rows={3} placeholder={t('enterNotes')} />
                    </Form.Item>

                    <Divider style={{ margin: '24px 0' }} />

                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space size="middle" style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button
                                onClick={() => setIsModalVisible(false)}
                                size="large"
                            >
                                {t('cancel')}
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                loading={createMutation.isLoading || isSubmitting}
                                disabled={createMutation.isLoading || isSubmitting}
                            >
                                {editingStudent ? t('update') : t('add')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Payment Modal */}
            <Modal
                title={null}
                open={isPaymentModalVisible}
                onCancel={() => {
                    setIsPaymentModalVisible(false);
                    setSelectedEnrollment(null);
                    paymentForm.resetFields();
                    setPaymentCalculation({ amount: 0, discount: 0, actualPayment: 0, newRemaining: 0 });
                }}
                footer={null}
                width={800}
                centered
                bodyStyle={{ padding: 0 }}
            >
                {selectedEnrollment && (
                    <>
                        {/* Header Section */}
                        <div style={{
                            background: 'linear-gradient(135deg, #2C5F9E 0%, #1e3a5f 100%)',
                            padding: '30px 32px',
                            borderTopLeftRadius: '8px',
                            borderTopRightRadius: '8px',
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div>
                                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                                        {t('recordPayment')}
                                    </div>
                                    <div style={{ fontSize: '24px', fontWeight: 700, color: 'white', marginBottom: '4px' }}>
                                        {selectedEnrollment.student.firstName} {selectedEnrollment.student.lastName}
                                    </div>
                                    <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.85)' }}>
                                        {coursesData?.find(c => c._id === (selectedEnrollment.enrollment.course?._id || selectedEnrollment.enrollment.course))?.name || 'Course'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div style={{ padding: '32px' }}>
                            {/* Current Balance Section */}
                            <div style={{ marginBottom: '32px' }}>
                                <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    {t('currentBalance')}
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                                    <div style={{
                                        background: '#f8fafc',
                                        border: '2px solid #e2e8f0',
                                        borderRadius: '12px',
                                        padding: '20px',
                                        textAlign: 'center',
                                        transition: 'all 0.3s ease'
                                    }}>
                                        <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            {t('courseAmount')}
                                        </div>
                                        <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b' }}>
                                            {formatCurrency(paymentCalculation.courseFee, selectedEnrollment.enrollment.payment?.currency)}
                                        </div>
                                    </div>
                                    <div style={{
                                        background: '#f0fdf4',
                                        border: '2px solid #86efac',
                                        borderRadius: '12px',
                                        padding: '20px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{ fontSize: '11px', color: '#166534', marginBottom: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            {t('paidAmount')}
                                        </div>
                                        <div style={{ fontSize: '20px', fontWeight: 700, color: '#15803d' }}>
                                            {formatCurrency(paymentCalculation.paidAmount, selectedEnrollment.enrollment.payment?.currency)}
                                        </div>
                                    </div>
                                    <div style={{
                                        background: '#fef2f2',
                                        border: '2px solid #fca5a5',
                                        borderRadius: '12px',
                                        padding: '20px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{ fontSize: '11px', color: '#991b1b', marginBottom: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            {t('remainingAmount')}
                                        </div>
                                        <div style={{ fontSize: '20px', fontWeight: 700, color: '#dc2626' }}>
                                            {formatCurrency(paymentCalculation.remainingAmount, selectedEnrollment.enrollment.payment?.currency)}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <Divider style={{ margin: '32px 0' }} />

                            {/* Payment Form */}
                            <Form form={paymentForm} layout="vertical" onFinish={handlePaymentSubmit}>
                                <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, marginBottom: '20px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    {t('paymentDetails')}
                                </div>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="discount"
                                            label={<span style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>{t('discountAmount')}</span>}
                                        >
                                            <InputNumber
                                                style={{ width: '100%', borderRadius: '10px', fontSize: '16px' }}
                                                size="large"
                                                min={0}
                                                prefix={selectedEnrollment?.enrollment?.payment?.currency === 'IQD' ? 'IQD' : '$'}
                                                placeholder="0.00"
                                                onChange={(value) => {
                                                    const remaining = Number(paymentCalculation.remainingAmount) || 0;
                                                    const discount = Math.max(0, value || 0);

                                                    // If discount covers the full remaining amount, set payment to 0
                                                    let amount;
                                                    if (discount >= remaining) {
                                                        amount = 0;
                                                        paymentForm.setFieldsValue({ amount: 0 });
                                                    } else {
                                                        // Otherwise, adjust payment to cover what discount doesn't
                                                        amount = remaining - discount;
                                                        paymentForm.setFieldsValue({ amount: amount });
                                                    }

                                                    const newRemaining = remaining - amount - discount;

                                                    setPaymentCalculation({
                                                        ...paymentCalculation,
                                                        discount,
                                                        amount: amount,
                                                        actualPayment: amount,
                                                        newRemaining
                                                    });
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="amount"
                                            label={<span style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>{t('payAmount')}</span>}
                                            rules={[
                                                { required: true, message: t('pleaseEnterAmount') }
                                            ]}
                                        >
                                            <InputNumber
                                                style={{ width: '100%', borderRadius: '10px', fontSize: '16px' }}
                                                size="large"
                                                min={0}
                                                prefix={selectedEnrollment?.enrollment?.payment?.currency === 'IQD' ? 'IQD' : '$'}
                                                placeholder="0.00"
                                                onChange={(value) => {
                                                    const remaining = Number(paymentCalculation.remainingAmount) || 0;
                                                    const amount = Math.max(0, value || 0);
                                                    const currentDiscount = paymentForm.getFieldValue('discount') || 0;

                                                    const newRemaining = remaining - amount - currentDiscount;

                                                    setPaymentCalculation({
                                                        ...paymentCalculation,
                                                        discount: currentDiscount,
                                                        amount: amount,
                                                        actualPayment: amount,
                                                        newRemaining
                                                    });
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                {/* New Remaining Balance Display */}
                                {(paymentCalculation.discount > 0 || paymentCalculation.amount > 0) && (
                                    <div style={{
                                        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                                        border: '2px solid #3b82f6',
                                        borderRadius: '12px',
                                        padding: '24px',
                                        marginBottom: '24px'
                                    }}>
                                        <Row gutter={16} style={{ marginBottom: '16px' }}>
                                            <Col span={8} style={{ textAlign: 'center' }}>
                                                <div style={{ fontSize: '11px', color: '#1e40af', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase' }}>
                                                    {t('discount')}
                                                </div>
                                                <div style={{ fontSize: '18px', fontWeight: 700, color: '#2563eb' }}>
                                                    - {formatCurrency(paymentCalculation.discount || 0, selectedEnrollment?.enrollment?.payment?.currency)}
                                                </div>
                                            </Col>
                                            <Col span={8} style={{ textAlign: 'center' }}>
                                                <div style={{ fontSize: '11px', color: '#1e40af', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase' }}>
                                                    {t('paying')}
                                                </div>
                                                <div style={{ fontSize: '18px', fontWeight: 700, color: '#2563eb' }}>
                                                    - {formatCurrency(paymentCalculation.actualPayment || 0, selectedEnrollment?.enrollment?.payment?.currency)}
                                                </div>
                                            </Col>
                                            <Col span={8} style={{ textAlign: 'center' }}>
                                                <div style={{ fontSize: '11px', color: '#1e40af', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase' }}>
                                                    {t('newRemaining')}
                                                </div>
                                                <div style={{ fontSize: '20px', fontWeight: 700, color: paymentCalculation.newRemaining === 0 ? '#15803d' : '#2563eb' }}>
                                                    {formatCurrency(paymentCalculation.newRemaining || 0, selectedEnrollment?.enrollment?.payment?.currency)}
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                )}

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="paymentMethod"
                                            label={<span style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>{t('paymentMethod')}</span>}
                                            rules={[{ required: true, message: t('pleaseSelectPaymentMethod') }]}
                                        >
                                            <Select size="large" style={{ borderRadius: '10px' }} placeholder={t('selectPaymentMethod')}>
                                                <Option value="cash">{t('cash')}</Option>
                                                <Option value="online">{t('onlinePayment')}</Option>
                                                <Option value="bank-transfer">{t('bankTransfer')}</Option>
                                                <Option value="card">{t('card')}</Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="paymentDate"
                                            label={<span style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>{t('paymentDate')}</span>}
                                            rules={[{ required: true, message: t('pleaseSelectDate') }]}
                                        >
                                            <DatePicker
                                                style={{ width: '100%', borderRadius: '10px' }}
                                                size="large"
                                                format="DD-MM-YYYY"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={24}>
                                        <Form.Item
                                            name="paymentDeadline"
                                            label={<span style={{ fontWeight: 600, fontSize: '14px', color: '#ea580c' }}>📅 {t('paymentDeadline') || 'Next Payment Deadline'}</span>}
                                            tooltip={t('paymentDeadlineTooltip') || 'Set deadline for the next payment installment. A notification will appear when it is due.'}
                                        >
                                            <DatePicker
                                                style={{ width: '100%', borderRadius: '10px' }}
                                                size="large"
                                                format="DD-MM-YYYY"
                                                disabledDate={(current) => current && current < dayjs().startOf('day')}
                                                placeholder={t('selectNextPaymentDeadline') || 'Select next payment deadline (optional)'}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item
                                    name="description"
                                    label={<span style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>{t('notes')}</span>}
                                >
                                    <Input.TextArea
                                        size="large"
                                        rows={3}
                                        placeholder={t('optionalNotes')}
                                        style={{ borderRadius: '10px' }}
                                    />
                                </Form.Item>

                                <Divider style={{ margin: '24px 0' }} />

                                <Form.Item style={{ marginBottom: 0 }}>
                                    <Space style={{ width: '100%', justifyContent: 'flex-end' }} size="middle">
                                        <Button
                                            onClick={() => {
                                                setIsPaymentModalVisible(false);
                                                setSelectedEnrollment(null);
                                                paymentForm.resetFields();
                                                setPaymentCalculation({ amount: 0, discount: 0, actualPayment: 0, newRemaining: 0 });
                                            }}
                                            size="large"
                                            style={{ borderRadius: '10px', minWidth: '120px', height: '44px', fontWeight: 600 }}
                                        >
                                            {t('cancel')}
                                        </Button>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            size="large"
                                            loading={paymentMutation.isLoading}
                                            icon={<DollarOutlined />}
                                            style={{
                                                background: 'linear-gradient(135deg, #2C5F9E 0%, #1e3a5f 100%)',
                                                border: 'none',
                                                borderRadius: '10px',
                                                minWidth: '180px',
                                                height: '44px',
                                                fontWeight: 600,
                                                fontSize: '15px'
                                            }}
                                        >
                                            {t('recordPayment')}
                                        </Button>
                                    </Space>
                                </Form.Item>
                            </Form>
                        </div>
                    </>
                )}
            </Modal>

            {/* Hidden certificates for printing */}
            <div style={{ display: 'none' }}>
                <div ref={certificatesPrintRef}>
                    {certificatesToPrint.map((cert, index) => {
                        const template = cert.template || templateData;
                        if (!template) return null;

                        const isLandscape = template.design?.orientation === 'landscape';

                        const processText = (text) => {
                            if (!text) return '';
                            return text
                                .replace('{{studentName}}', `${cert.student?.firstName} ${cert.student?.lastName}`)
                                .replace('{{courseName}}', cert.course?.name)
                                .replace('{{completionDate}}', dayjs(cert.completionDate).format('MMMM DD, YYYY'))
                                .replace('{{issueDate}}', dayjs(cert.issueDate).format('MMMM DD, YYYY'))
                                .replace('{{certificateNumber}}', cert.certificateNumber)
                                .replace('{{grade}}', cert.grade || 'Pass');
                        };

                        return (
                            <div key={cert._id} style={{ pageBreakAfter: index < certificatesToPrint.length - 1 ? 'always' : 'auto' }}>
                                <style>
                                    {`
                                        @media print {
                                            @page {
                                                size: A4 ${isLandscape ? 'landscape' : 'portrait'};
                                                margin: 0;
                                            }
                                        }
                                    `}
                                </style>
                                <div style={{
                                    width: isLandscape ? '297mm' : '210mm',
                                    height: isLandscape ? '210mm' : '297mm',
                                    padding: '15mm',
                                    margin: '0 auto',
                                    backgroundColor: template.design?.backgroundColor || '#fff',
                                    border: `${template.design?.borderWidth || 3}px solid ${template.design?.borderColor || '#1890ff'}`,
                                    borderRadius: '10px',
                                    fontFamily: template.body?.text?.font?.family || 'Arial, sans-serif',
                                    position: 'relative',
                                    boxSizing: 'border-box'
                                }}>
                                    <div style={{ textAlign: 'center', marginBottom: '25px' }}>
                                        {template.header?.logo && (
                                            <div style={{ marginBottom: '15px' }}>
                                                <img
                                                    src={`${user.academy.logo}${template.header.logo}`}
                                                    alt=""
                                                    crossOrigin="anonymous"
                                                    style={{
                                                        maxHeight: '100px',
                                                        maxWidth: '200px',
                                                        height: 'auto',
                                                        width: 'auto',
                                                        display: 'block',
                                                        margin: '0 auto',
                                                        objectFit: 'contain'
                                                    }}
                                                />
                                            </div>
                                        )}
                                        <h1 style={{
                                            fontSize: `${template.header?.instituteNameFont?.size || 40}px`,
                                            color: template.header?.instituteNameFont?.color || '#1890ff',
                                            margin: '0',
                                            fontWeight: 'bold',
                                            fontFamily: template.header?.instituteNameFont?.family || 'Arial'
                                        }}>
                                            {template.header?.instituteName || 'Language Institute'}
                                        </h1>
                                    </div>

                                    <div style={{ textAlign: 'center', marginBottom: '35px' }}>
                                        <h2 style={{
                                            fontSize: `${template.body?.title?.font?.size || 32}px`,
                                            color: template.body?.title?.font?.color || '#333',
                                            margin: '0',
                                            textTransform: 'uppercase',
                                            letterSpacing: '2px',
                                            fontFamily: template.body?.title?.font?.family || 'Arial'
                                        }}>
                                            {template.body?.title?.text || 'Certificate of Completion'}
                                        </h2>
                                    </div>

                                    <div style={{
                                        fontSize: `${template.body?.text?.font?.size || 18}px`,
                                        lineHeight: '1.8',
                                        textAlign: 'center',
                                        marginBottom: '35px',
                                        color: template.body?.text?.font?.color || '#333',
                                        fontFamily: template.body?.text?.font?.family || 'Arial',
                                        whiteSpace: 'pre-line'
                                    }}>
                                        {processText(template.body?.text?.template)}
                                    </div>

                                    <div style={{
                                        position: 'absolute',
                                        bottom: '15mm',
                                        left: '15mm',
                                        right: '15mm',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-end'
                                    }}>
                                        <div style={{ textAlign: 'left' }}>
                                            <p style={{ fontSize: '14px', color: '#666', margin: '0' }}>
                                                {t('certificateNo')}: {cert.certificateNumber}
                                            </p>
                                            {template.footer?.showDate && (
                                                <p style={{ fontSize: '14px', color: '#666', margin: '5px 0 0 0' }}>
                                                    {t('issueDate')}: {dayjs(cert.issueDate).format('YYYY-MM-DD')}
                                                </p>
                                            )}
                                            <p style={{ fontSize: '12px', color: '#999', margin: '5px 0 0 0' }}>
                                                {t('verificationCode')}: {cert.verificationCode}
                                            </p>
                                        </div>
                                        {template.footer?.showSignature && (
                                            <div style={{ textAlign: 'right' }}>
                                                {template.footer?.signatureImage ? (
                                                    <div>
                                                        <img
                                                            src={`${user.academy.logo}${template.footer.signatureImage}`}
                                                            alt=""
                                                            crossOrigin="anonymous"
                                                            style={{ maxHeight: '60px', marginBottom: '10px' }}
                                                        />
                                                        <p style={{ fontSize: '14px', margin: '0', borderTop: '2px solid #333', paddingTop: '5px' }}>
                                                            {template.footer?.signatureLabel || t('authorizedSignature')}
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <div style={{
                                                        borderTop: '2px solid #333',
                                                        paddingTop: '10px',
                                                        minWidth: '200px'
                                                    }}>
                                                        <p style={{ fontSize: '16px', margin: '0' }}>
                                                            {template.footer?.signatureLabel || t('authorizedSignature')}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {(template.design?.layout === 'classic' || template.design?.layout === 'modern') && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%) rotate(-45deg)',
                                            opacity: 0.03,
                                            fontSize: '120px',
                                            fontWeight: 'bold',
                                            color: template.design?.borderColor || '#1890ff',
                                            pointerEvents: 'none',
                                            zIndex: 0
                                        }}>
                                            {t('certificates')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default Students;
